/**
 * HiBFF V3 - API Client
 * Handles all communication with the FastAPI backend
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * FastAPI validation errors (422) return `detail` as an array of objects —
 * rendering that raw gives "[object Object]". Flatten to a readable string.
 */
const formatApiDetail = (detail) => {
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d && typeof d === 'object' ? d.msg || JSON.stringify(d) : String(d)))
      .join(' · ');
  }
  if (detail && typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
  return detail;
};

/**
 * Returns a `fetch` implementation that isn't affected by scripts that
 * monkey-patch window.fetch (Emergent recorder, rrweb, analytics, etc.).
 *
 * Strategy — in order of preference:
 *   1. The native fetch captured in index.html BEFORE any other script loaded
 *      (window.__HIBFF_NATIVE_FETCH__)
 *   2. A pristine fetch from a hidden iframe (some CSPs block this)
 *   3. Last resort: the current window.fetch (may be wrapped, but still works
 *      99% of the time — only the edge case of double-clone throws)
 */
let _cleanFetch = null;
function getCleanFetch() {
  if (_cleanFetch) return _cleanFetch;

  // Tier 1 — native reference captured in index.html before any script ran
  if (typeof window !== 'undefined' && typeof window.__HIBFF_NATIVE_FETCH__ === 'function') {
    _cleanFetch = window.__HIBFF_NATIVE_FETCH__;
    return _cleanFetch;
  }

  // Tier 2 — iframe trick
  try {
    if (typeof document === 'undefined') {
      _cleanFetch = window.fetch.bind(window);
      return _cleanFetch;
    }
    const frame = document.createElement('iframe');
    frame.style.display = 'none';
    frame.setAttribute('aria-hidden', 'true');
    document.body.appendChild(frame);
    const frameFetch = frame.contentWindow && frame.contentWindow.fetch;
    if (frameFetch) {
      _cleanFetch = frameFetch.bind(window);
    } else {
      _cleanFetch = window.fetch.bind(window);
    }
  } catch {
    _cleanFetch = window.fetch.bind(window);
  }
  return _cleanFetch;
}

class ApiClient {
  constructor() {
    this.baseUrl = API_BASE_URL;
    // Sprint 182 — auth drift detection.
    // Track whether /api/users/me has succeeded recently. If a subsequent
    // /api/admin/* or /api/vault/* call returns 401 within the trust
    // window, we know the session went dark mid-request and surface a
    // banner via the `hibff:auth-drift` custom event.
    this._meSucceededAt = 0;
    // In-flight GET dedupe — React 18 strict-mode double-mounts fire the
    // same GET twice within the same tick; sharing the pending promise
    // eliminates the duplicate network call and the ERR_ABORTED noise.
    this._inflight = new Map();
  }

  _markMeSucceeded() {
    this._meSucceededAt = Date.now();
  }

  _isInTrustWindow() {
    // 10-minute window: if /me succeeded within the last 10 min, any 401
    // on an admin/vault endpoint is treated as drift.
    return Date.now() - this._meSucceededAt < 10 * 60 * 1000;
  }

  _dispatchAuthDrift(url) {
    try {
      window.dispatchEvent(new CustomEvent('hibff:auth-drift', { detail: { url, at: new Date().toISOString() } }));
    } catch (e) {
      // Swallow — drift signalling must never break the original request flow.
    }
  }

  _dispatchVaultLocked(url) {
    // Sprint 182 — informational signal: a /api/vault/* endpoint 401'd,
    // which means the Vault TOTP gate is closed (NOT that the admin
    // session is dead). The subtle blue VaultLockedChip listens for this
    // event and surfaces a "click to unlock" CTA in the corner.
    try {
      window.dispatchEvent(new CustomEvent('hibff:vault-locked', { detail: { url, at: new Date().toISOString() } }));
    } catch (e) {
      // Swallow — best-effort UI hint.
    }
  }

  getToken() {
    // Check both storage locations
    const persistent = localStorage.getItem('hibff_auth_token');
    const session = sessionStorage.getItem('hibff_auth_token');
    return persistent || session;
  }

  setToken(token, rememberMe = false) {
    if (rememberMe) {
      localStorage.setItem('hibff_auth_token', token);
      sessionStorage.removeItem('hibff_auth_token');
    } else {
      sessionStorage.setItem('hibff_auth_token', token);
      localStorage.removeItem('hibff_auth_token');
    }
  }

  clearToken() {
    localStorage.removeItem('hibff_auth_token');
    sessionStorage.removeItem('hibff_auth_token');
  }

  /**
   * Sprint 208A — Magic-link / unauthenticated public endpoints.
   * Skips localStorage token entirely so a stale parent/admin token can't
   * accidentally hijack a public token-scoped flow (the /onboarding/console
   * routes authenticate via the URL token alone).
   */
  async requestPublic(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    const response = await getCleanFetch()(url, { ...options, headers });
    let data;
    try { data = await response.json(); } catch { data = { detail: response.statusText || 'API_ERROR' }; }
    if (!response.ok) {
      const err = new Error(formatApiDetail(data?.detail) || data?.message || `HTTP ${response.status}`);
      err.status = response.status;
      err.data = data;
      if (response.status === 403 && typeof data?.detail === 'string') {
        const code = ['age_declaration_required', 'age_out_of_range', 'parent_consent_pending']
          .find((c) => data.detail.startsWith(c));
        if (code) window.dispatchEvent(new CustomEvent('hibff:verification-gate', { detail: { code } }));
      }
      throw err;
    }
    return data;
  }

  async request(endpoint, options = {}) {
    const method = (options.method || 'GET').toUpperCase();
    if (method !== 'GET') {
      return this._performRequest(endpoint, options);
    }
    if (this._inflight.has(endpoint)) {
      return this._inflight.get(endpoint);
    }
    const promise = this._performRequest(endpoint, options).finally(() => {
      this._inflight.delete(endpoint);
    });
    this._inflight.set(endpoint, promise);
    return promise;
  }

  async _performRequest(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }    try {
      const fetchConfig = {
        ...options,
        headers,
        // Sprint 182 — same-origin auth cookie redundancy.
        // The admin-direct success path sets an HttpOnly `hibff_auth` cookie
        // alongside the localStorage write. `credentials: 'include'` makes
        // sure that cookie travels with every /api/* call, so the admin
        // session keeps working even if localStorage was wiped by an
        // extension, incognito storage partitioning, or a transient 401
        // cascade. The backend's `get_current_user` accepts either channel.
        credentials: 'include',
      };

      let response;
      try {
        response = await getCleanFetch()(url, fetchConfig);
      } catch (netErr) {
        // "TypeError: Failed to fetch" with a healthy backend = either a
        // transient connection reset OR the cached _cleanFetch binding went
        // dead (wrapped/patched fetch, detached iframe). Both are fatal for
        // the WHOLE session if we keep the poisoned binding — so reset it
        // and retry GETs twice on the live window.fetch with short backoff.
        const method = (options.method || 'GET').toUpperCase();
        if (method !== 'GET' || !(netErr instanceof TypeError)) throw netErr;
        _cleanFetch = null;
        let lastErr = netErr;
        for (const delayMs of [250, 750]) {
          await new Promise((r) => setTimeout(r, delayMs));
          try {
            response = await window.fetch.bind(window)(url, fetchConfig);
            _cleanFetch = window.fetch.bind(window);
            break;
          } catch (e2) {
            lastErr = e2;
          }
        }
        if (!response) throw lastErr;
      }

      // Parse JSON response (fall back to text if body isn't JSON)
      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        // Body was already consumed by a service worker, or isn't JSON
        data = { detail: response.statusText || 'API_ERROR' };
      }

      // Handle 401 Unauthorized — surface the backend's detail (e.g. "Invalid email or password")
      if (response.status === 401) {
        // Sprint 182 — auth drift detection.
        // If /api/users/me succeeded recently AND now an /api/admin/* data
        // endpoint is 401-ing, the session has gone dark mid-flight (the
        // exact "Command Centre is empty" signature). Fire the drift
        // event so AuthDriftBanner can surface a sticky re-sign-in CTA
        // instead of leaving the user staring at silent empty tables.
        //
        // We deliberately EXCLUDE /api/vault/* from drift detection.
        // Vault endpoints sit behind a second TOTP gate (`verify_vault_access`)
        // so a 401 there means "Vault is locked, enter TOTP" — NOT "your
        // admin session is dead". Treating those as drift produced false
        // positives whenever the founder browsed the Command Centre
        // without unlocking the Vault first. Instead, /api/vault/* 401s
        // get their own subtle informational chip.
        const isAdminEndpoint = /\/api\/admin\b/.test(url);
        const isVaultEndpoint = /\/api\/vault\b/.test(url);
        if (isAdminEndpoint && this._isInTrustWindow()) {
          this._dispatchAuthDrift(url);
        }
        if (isVaultEndpoint && this._isInTrustWindow()) {
          // Skip the chip on the TOTP-gate bootstrap endpoints — those are
          // expected to 401 on first load and produce noisy chips.
          const vaultBootstrapPaths = [
            '/api/vault/session-check',
            '/api/vault/totp/setup',
            '/api/vault/totp/verify',
          ];
          const isBootstrap = vaultBootstrapPaths.some((p) => url.endsWith(p));
          if (!isBootstrap) {
            this._dispatchVaultLocked(url);
          }
        }

        // Sprint 182 — be defensive about clearing the token.
        // With the same-origin `hibff_auth` cookie redundancy in place, a
        // 401 from a single data endpoint is no longer a reliable signal
        // that "the session is dead" — it could be a stale role check, a
        // route that requires a higher privilege, a transient backend
        // restart, or a clock-skew/JWT-iat race. We now ONLY clear the
        // token for explicit credential-validation paths (login + change
        // password). Everywhere else, let the page surface the 401 and
        // let AuthContext be the sole arbiter of "should the user be
        // bounced to the sign-in screen".
        const clearOnFailPaths = [
          '/api/auth/login',
          '/api/auth/verify-2fa',
          '/api/auth/verify-2fa-recovery',
          '/api/users/me/change-password',
        ];
        const shouldClear = clearOnFailPaths.some((p) => url.endsWith(p));
        if (shouldClear) {
          this.clearToken();
        }
        const detail = formatApiDetail(data && data.detail) || 'Unauthorized';
        const err = new Error(detail);
        err.status = 401;
        throw err;
      }

      if (!response.ok) {
        throw new Error(formatApiDetail(data.detail) || 'API_ERROR');
      }

      // Mark /api/users/me success for the auth-drift trust window.
      if (url.endsWith('/api/users/me')) {
        this._markMeSucceeded();
      }

      return data;
    } catch (error) {
      if (error.status === 401) {
        throw error;
      }
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth endpoints
  async register(userData) {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(email, password) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout() {
    return this.request('/api/auth/logout', {
      method: 'POST',
    });
  }

  // User endpoints
  async getCurrentUser() {
    return this.request('/api/users/me');
  }

  async updateProfile(profileData) {
    return this.request('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify(profileData),
    });
  }

  async getDashboardData() {
    return this.request('/api/users/me/dashboard');
  }

  async getMoodHistory(days = 7) {
    return this.request(`/api/users/me/mood?days=${days}`);
  }

  async getBadges() {
    return this.request('/api/users/me/badges');
  }

  async getActivityFeed(limit = 10) {
    return this.request(`/api/users/me/activity?limit=${limit}`);
  }

  async requestDataDeletion() {
    return this.request('/api/users/me/data', {
      method: 'DELETE',
    });
  }

  // AVA endpoints
  async getAvas() {
    return this.request('/api/avas');
  }

  async createAva(avaData) {
    return this.request('/api/avas', {
      method: 'POST',
      body: JSON.stringify(avaData),
    });
  }

  async getAva(avaId) {
    return this.request(`/api/avas/${avaId}`);
  }

  async updateAva(avaId, avaData) {
    return this.request(`/api/avas/${avaId}`, {
      method: 'PATCH',
      body: JSON.stringify(avaData),
    });
  }

  async deleteAva(avaId) {
    return this.request(`/api/avas/${avaId}`, {
      method: 'DELETE',
    });
  }

  // Chat endpoints
  async sendChatMessage(sessionId, message) {
    return this.request('/api/chat', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, message }),
    });
  }

  async hibffChat(userId, avatarId, mode, message, history = null, language = null, avatarName = null, sessionId = null) {
    return this.request('/api/hibff-chat', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        avatarId,
        mode,
        message,
        history,
        language,
        avatarName,
        sessionId,
      }),
    });
  }

  async getChatHistory(sessionId, limit = 50) {
    return this.request(`/api/chat/${sessionId}/history?limit=${limit}`);
  }

  async startChatSession(sessionId) {
    return this.request(`/api/chat/${sessionId}/start`, {
      method: 'POST',
    });
  }

  // Session endpoints
  async getSessions(limit = 20, statusFilter = null) {
    let url = `/api/sessions?limit=${limit}`;
    if (statusFilter) {
      url += `&status_filter=${statusFilter}`;
    }
    return this.request(url);
  }

  async createSession(sessionData) {
    return this.request('/api/sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  }

  async getSession(sessionId) {
    return this.request(`/api/sessions/${sessionId}`);
  }

  async endSession(sessionId) {
    return this.request(`/api/sessions/${sessionId}/end`, {
      method: 'PATCH',
    });
  }

  async getRecentConversations(limit = 5) {
    return this.request(`/api/sessions/conversations/recent?limit=${limit}`);
  }

  async toggleFavouriteConversation(conversationId) {
    return this.request(`/api/sessions/conversations/${conversationId}/favourite`, { method: 'POST' });
  }

  async deleteConversation(conversationId) {
    return this.request(`/api/sessions/conversations/${conversationId}`, { method: 'DELETE' });
  }

  // Social Score endpoints
  async getCurrentScore() {
    return this.request('/api/scores/current');
  }

  async getScoreHistory(limit = 30) {
    return this.request(`/api/scores/history?limit=${limit}`);
  }

  async getGoals(statusFilter = null) {
    let url = '/api/scores/goals';
    if (statusFilter) {
      url += `?status_filter=${statusFilter}`;
    }
    return this.request(url);
  }

  async createGoal(goalData) {
    return this.request('/api/scores/goals', {
      method: 'POST',
      body: JSON.stringify(goalData),
    });
  }

  async updateGoal(goalId, goalData) {
    return this.request(`/api/scores/goals/${goalId}`, {
      method: 'PATCH',
      body: JSON.stringify(goalData),
    });
  }

  async deleteGoal(goalId) {
    return this.request(`/api/scores/goals/${goalId}`, {
      method: 'DELETE',
    });
  }

  // Dashboard endpoints
  async logMood(moodData) {
    return this.request('/api/dashboard/mood', {
      method: 'POST',
      body: JSON.stringify(moodData),
    });
  }

  async getMoodHistory(days = 7) {
    return this.request(`/api/dashboard/mood/history?days=${days}`);
  }

  async getActivityFeed(limit = 10) {
    return this.request(`/api/dashboard/activity?limit=${limit}`);
  }

  async dailyCheckin(checkinData) {
    return this.request('/api/dashboard/checkin', {
      method: 'POST',
      body: JSON.stringify(checkinData),
    });
  }

  async getStreakInfo() {
    return this.request('/api/dashboard/streak');
  }

  async getNotifications(limit = 10) {
    return this.request(`/api/dashboard/notifications?limit=${limit}`);
  }

  async markNotificationsRead(notificationIds) {
    return this.request('/api/dashboard/notifications/read', {
      method: 'POST',
      body: JSON.stringify({ notification_ids: notificationIds, mark_as_read: true }),
    });
  }

  async getDashboardPreferences() {
    return this.request('/api/dashboard/preferences');
  }

  async saveDashboardPreferences(prefs) {
    return this.request('/api/dashboard/preferences', {
      method: 'POST',
      body: JSON.stringify(prefs),
    });
  }

  // Social Connections endpoints
  async createInvitation(invitationData) {
    return this.request('/api/connections/invite', {
      method: 'POST',
      body: JSON.stringify(invitationData),
    });
  }

  async getInvitation(inviteCode) {
    return this.request(`/api/connections/invite/${inviteCode}`);
  }

  async respondToInvitation(inviteCode, response) {
    return this.request(`/api/connections/invite/${inviteCode}/respond`, {
      method: 'POST',
      body: JSON.stringify(response),
    });
  }

  async getConnections() {
    return this.request('/api/connections/');
  }

  async getPendingInvitations() {
    return this.request('/api/connections/pending');
  }

  async setConnectionAva(connectionId, avaId) {
    return this.request(`/api/connections/${connectionId}/set-ava?ava_id=${avaId}`, {
      method: 'POST',
    });
  }

  async graduateConnection(connectionId) {
    return this.request(`/api/connections/${connectionId}/graduate`, {
      method: 'POST',
    });
  }

  // Challenges API
  async getTodaysChallenge() {
    return this.request('/api/challenges/today');
  }

  async logChallenge(data) {
    return this.request('/api/challenges/log', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getChallengeLogs(days = 7) {
    return this.request(`/api/challenges/log?days=${days}`);
  }

  async getChallengeStats() {
    return this.request('/api/challenges/stats');
  }

  async getChallengeActionTypes() {
    return this.request('/api/challenges/action-types');
  }

  // Health check
  async healthCheck() {
    return this.request('/api/health');
  }

  // Billing API
  async getPlans() {
    return this.request('/api/billing/plans');
  }

  async subscribe(planId, promoCode = null) {
    return this.request('/api/billing/subscribe', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId, promo_code: promoCode }),
    });
  }

  async getSubscription() {
    return this.request('/api/billing/subscription');
  }

  async buyHearts(packId) {
    return this.request('/api/billing/buy-hearts', {
      method: 'POST',
      body: JSON.stringify({ pack_id: packId }),
    });
  }

  async getHeartsPacks() {
    return this.request('/api/billing/hearts-packs');
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
